import { useIsAuthenticated } from '@azure/msal-react'
import { useEffect, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useProfile } from '../../hooks/useProfile'
import { msalInstance, loginRequest } from '../../lib/msalConfig'

interface AuthGuardProps {
  children: ReactNode
}

export function AuthGuard({ children }: AuthGuardProps) {
  const isAuthenticated = useIsAuthenticated()
  const navigate = useNavigate()
  const { profile, isLoading, error } = useProfile()

  // Issue #43 — plus de `loginRedirect()` déclenché à la main ici. Le `.catch(console.error)` ne
  // captait pas le retour d'Entra en erreur : l'utilisateur restait non authentifié et repartait en
  // redirection sans fin. Ce cas est désormais servi par `LoginPage` (route `*` de l'arbre non
  // authentifié), dont la garde vient de `useMsalAuthentication`. Ici on se contente de ne rien
  // rendre : `main.tsx` bascule sur l'arbre non authentifié, qui affiche l'écran ou l'erreur.

  useEffect(() => {
    if (!isLoading && error?.status === 403) {
      navigate('/access-denied', { replace: true })
    }
  }, [isLoading, error, navigate])

  if (!isAuthenticated) return null
  if (isLoading) return (
    <div className="flex items-center justify-center h-screen bg-background">
      <p className="text-muted-foreground font-body">Chargement...</p>
    </div>
  )
  // Issue #43 — « Rechargez la page » était une IMPASSE sur un 401. ⚠️ Portée réelle, établie par le
  // test T5 du 2026-08-29 : cette branche n'est atteinte qu'au **chargement initial du profil**.
  // Une perte de session survenant plus tard, le profil étant déjà en contexte, ne passe PAS ici —
  // elle est traitée par `recoverSession` dans `apiClient`. Ce bouton reste la sortie du cas initial.
  // Depuis que `getToken()` ne
  // redirige plus que sur `InteractionRequiredAuthError`, une expiration de session qui sort
  // autrement (`monitor_window_timeout` de l'iframe silencieuse, `post_request_failed`, ou un
  // `AADSTS650052` obtenu silencieusement après révocation du consentement) remonte ici en 401 :
  // recharger rejoue la même séquence, et plus rien ne relance de login. Le bouton rend la sortie
  // possible, et comme il exige un geste de l'utilisateur il ne peut pas reboucler.
  if (error && error.status !== 403) return (
    <div className="flex flex-col items-center justify-center h-screen bg-background px-4 gap-4">
      <p className="text-muted-foreground font-body text-center">
        Erreur lors du chargement du profil ({error.status}).
      </p>
      {error.status === 401 ? (
        <button
          type="button"
          onClick={() => { void msalInstance.loginRedirect(loginRequest).catch(() => {}) }}
          className="px-6 py-2 bg-primary text-white font-body rounded-md hover:bg-accent transition-colors"
        >
          Se reconnecter
        </button>
      ) : (
        <p className="text-muted-foreground font-body text-sm">Rechargez la page.</p>
      )}
    </div>
  )
  if (!profile) return null

  return <>{children}</>
}
